import React, { useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TouchableOpacity, ActivityIndicator, Image, Dimensions, Alert, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { Colors } from '../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { useAppStore } from '../store/useAppStore';
import { storageService } from '../services/storageService';
import * as ImagePicker from 'expo-image-picker';
import { volver } from '../utils/volver';
import { mensajeDeError } from '../utils/mensajeDeError';

const { width } = Dimensions.get('window');

/**
 * Captura biométrica de foto de perfil.
 * AUDITORÍA — Antes esta pantalla usaba CameraView de expo-camera dentro
 * de una View nested con `StyleSheet.absoluteFillObject`. En Android eso
 * provocaba que la cámara renderizara como una pantalla AZUL sin
 * preview de cámara (mismo bug ya documentado en kyc-biometrics.tsx).
 *
 * Ahora usa ImagePicker.launchCameraAsync que abre la app de cámara
 * nativa del sistema — comportamiento idéntico al usado en
 * kyc-documents.tsx que ya funciona. Galería deshabilitada (cameraType
 * fija a Front, mediaTypes solo Images en vivo).
 *
 * Si el user ya hizo KYC biométrico, el backend YA le puso su selfie
 * como avatar automáticamente. Esta pantalla queda como fallback para
 * cambiar el avatar después.
 */
export default function ProfilePhotoCaptureScreen() {
  const router = useRouter();
  const setProfilePhotoTaken = useAppStore((state) => state.setProfilePhotoTaken);
  const setUserPhoto = useAppStore((state) => state.setUserPhoto);

  const [capturedUri, setCapturedUri] = useState<string | null>(null);
  const [capturedBase64, setCapturedBase64] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { userRole: role, userFirebaseUid } = useAppStore();
  const isVerified = useAppStore((state) => state.isVerified);

  const handleCapture = async () => {
    try {
      // 1) Permiso de cámara (Android exige pedirlo runtime).
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      if (perm.status !== 'granted') {
        Alert.alert(
          'Permiso de cámara denegado',
          'Habilita el permiso desde Configuración → Aplicaciones → Go Safely → Permisos → Cámara.'
        );
        return;
      }

      // 2) Abrir cámara nativa del sistema. Galería bloqueada — solo
      //    foto en vivo. Sin permisión a galería los anti-fraude
      //    (pegar foto vieja) quedan cerrados.
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.7,
        base64: true,
        cameraType: ImagePicker.CameraType.front,
      });
      if (result.canceled || !result.assets?.[0]) return;

      setCapturedUri(result.assets[0].uri);
      setCapturedBase64(result.assets[0].base64 ?? null);
    } catch (e: any) {
      Alert.alert('Error', mensajeDeError(e, 'No pudimos abrir la cámara. Revisa que Go Safely tenga permiso de cámara e intenta de nuevo.'));
    }
  };

  const handleRetake = () => {
    setCapturedUri(null);
    setCapturedBase64(null);
  };

  const handleConfirm = async () => {
    if (!userFirebaseUid) {
      Alert.alert('Sin sesión', 'No detectamos tu sesión. Cierra la app y vuelve a iniciar sesión.');
      return;
    }
    if (!capturedBase64) {
      Alert.alert('Foto no capturada', 'Toca el botón de captura primero.');
      return;
    }

    setIsUploading(true);
    try {
      // storageService.uploadProfilePhoto rutea por backend Admin SDK.
      const dataUri = `data:image/jpeg;base64,${capturedBase64}`;
      const downloadUrl = await storageService.uploadProfilePhoto(userFirebaseUid, dataUri);

      setUserPhoto(downloadUrl);
      setProfilePhotoTaken(true);

      // FIX FLUJO — Antes el fallback siempre rotuaba a /kyc-welcome (vehicle
      // picker). Si un chofer YA verificado tocaba "cambiar foto de perfil"
      // desde Edit Profile, terminaba en el picker como si tuviera que
      // re-elegir vehículo. Ahora distinguimos:
      //   - Usuario VERIFICADO cambiando foto → volver(router) vuelve a donde
      //     estaba (Edit Profile, Perfil, etc.) sin reempezar onboarding.
      //   - Usuario NUEVO post-signup que sigue el flujo de KYC → continúa
      //     a /kyc-welcome para completar la verificación.
      if (isVerified) {
        if (router.canGoBack()) {
          volver(router);
        } else {
          // Sin back-stack, llevar a su dashboard.
          if (role === 'RENT_CAR_OWNER') router.replace('/(tabs)/dashboard');
          else if (role === 'CAR_RENTER') router.replace('/(tabs)/marketplace');
          else if (role === 'SCHOOL_DIRECTOR') router.replace('/school-director-dashboard');
          else if (role === 'FOOD_DELIVERY') router.replace('/(tabs)/food');
          else if (role === 'RESTAURANT_OWNER') router.replace('/restaurant-dashboard');
          else router.replace('/(tabs)/map'); // PASSENGER, DRIVER, SCHOOL_DRIVER, DELIVERY_DRIVER
        }
      } else {
        // Usuario nuevo aún sin KYC — continuar al embudo.
        router.replace('/kyc-welcome');
      }
    } catch (error: any) {
      console.warn('[profile-photo-capture] upload error:', error?.message);
      Alert.alert(
        'No se pudo subir la foto',
        error?.message?.includes?.('Sesión')
          ? error.message
          : 'Hubo un problema al subir tu foto. Verifica tu conexión y vuelve a intentar.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color="#FFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Foto de Perfil en Vivo</Text>
        <Text style={styles.headerSubtitle}>
          Toma una selfie frontal — galería deshabilitada por seguridad.
        </Text>
      </View>

      <View style={styles.previewContainer}>
        {capturedUri ? (
          <View style={styles.previewBox}>
            <Image source={{ uri: capturedUri }} style={styles.previewImage} />
            {isUploading && (
              <View style={styles.uploadingOverlay}>
                <ActivityIndicator size="small" color="#FFF" style={{ marginBottom: 8 }} />
                <Text style={styles.uploadingText}>Subiendo tu foto...</Text>
              </View>
            )}
          </View>
        ) : (
          <View style={styles.previewBox}>
            <Ionicons name="person-circle" size={140} color="rgba(255,255,255,0.15)" />
            <Text style={styles.previewHint}>
              Toca "Tomar foto" para abrir la cámara frontal de tu Motorola.
            </Text>
          </View>
        )}
      </View>

      <View style={styles.footer}>
        <View style={styles.warningBox}>
          <Ionicons name="shield-half" size={18} color="#FF9500" style={{ marginRight: 8 }} />
          <Text style={styles.warningText}>
            Galería deshabilitada. Prohibido subir fotos antiguas, capturas de pantalla o con filtros.
          </Text>
        </View>

        {!capturedUri ? (
          <TouchableOpacity style={styles.captureBtn} onPress={handleCapture} activeOpacity={0.85}>
            <Ionicons name="camera" size={22} color="#FFF" style={{ marginRight: 8 }} />
            <Text style={styles.captureBtnText}>Tomar foto</Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.actionsRow}>
            <TouchableOpacity
              style={[styles.actionBtn, { borderColor: Colors.border, borderWidth: 1 }]}
              onPress={handleRetake}
              disabled={isUploading}
            >
              <Ionicons name="refresh" size={20} color={Colors.text} style={{ marginRight: 8 }} />
              <Text style={[styles.actionBtnText, { color: Colors.text }]}>Repetir</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionBtn, { backgroundColor: Colors.accent }]}
              onPress={handleConfirm}
              disabled={isUploading}
            >
              {isUploading ? (
                <ActivityIndicator size="small" color="#FFF" />
              ) : (
                <>
                  <Ionicons name="checkmark" size={20} color="#FFF" style={{ marginRight: 8 }} />
                  <Text style={[styles.actionBtnText, { color: '#FFF' }]}>Confirmar</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.primary,
  },
  header: {
    padding: 20,
    paddingTop: Platform.OS === 'ios' ? 10 : 30,
  },
  backBtn: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  headerTitle: {
    color: '#FFF',
    fontSize: 22,
    fontWeight: 'bold',
    marginTop: 8,
    textAlign: 'center',
  },
  headerSubtitle: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 18,
    marginTop: 6,
  },
  previewContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  previewBox: {
    width: width - 48,
    height: (width - 48) * 1.2,
    backgroundColor: '#0A1E30',
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  previewHint: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 13,
    marginTop: 16,
    textAlign: 'center',
    paddingHorizontal: 24,
    lineHeight: 19,
  },
  uploadingOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadingText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  footer: {
    padding: 20,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF3E0',
    padding: 12,
    borderRadius: 12,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#FFE0B2',
  },
  warningText: {
    flex: 1,
    fontSize: 11,
    color: '#E65100',
    fontWeight: '600',
    lineHeight: 16,
  },
  captureBtn: {
    height: 52,
    borderRadius: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.accent,
  },
  captureBtnText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  actionBtn: {
    flex: 1,
    height: 52,
    borderRadius: 14,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionBtnText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
});
